<script setup lang="ts">
import { $isAutoLinkNode, $isLinkNode, AutoLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $isListItemNode, $isListNode, ListNode, type ListType } from "@lexical/list";
import { $isHeadingNode } from "@lexical/rich-text";
import { $getSelectionStyleValueForProperty } from "@lexical/selection";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import {
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  type LexicalNode,
  type RangeSelection,
  SELECTION_CHANGE_COMMAND,
} from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";

import ChangeColorDropdown from "~/components/dumb/ChangeColorDropdown.vue";
import DropdownMenu from "~/components/dumb/DropdownMenu.vue";
import GenericSubdropdown from "~/components/dumb/GenericSubdropdown.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import {
  EVENT_CONVERT_TO_TASKS,
  EVENT_FORMAT_BACKGROUND_COLOR,
  EVENT_FORMAT_TEXT_COLOR,
  EVENT_GENERATE_RECOMMENDATION,
  EVENT_SPLIT_TASK,
  LEXICAL_OUTSIDE_CLASS,
  RECOMMENDATION_SECTION_KEY_AI,
} from "~/components/text/const";
import TextFormatOptionsDropdown from "~/components/text/plugins/TextFormatOptionsDropdown.vue";
import {
  formatBulletList,
  formatCheckList,
  formatNumberedList,
  getRecommendationTypeaheadSections,
  getSelectedNode,
} from "~/components/text/utils";
import {
  BoldIcon,
  ChecklistIcon,
  ChevronDownIcon,
  CodeSnippetIcon,
  ColorPickerIcon,
  ItalicIcon,
  LinkIcon,
  NumberedListIcon,
  RecommendationIcon,
  StrikethroughIcon,
  UnderlineIcon,
  UnorderedListIcon,
} from "~/icons";
import { CommandId, DropdownMenuItemKind, Language, Placement, RecommendationKind } from "~/shared/enums";
import type { TextBlockType, TypeaheadOption } from "~/shared/types";
import { useUserStore } from "~/stores";
import { BACKGROUND_ALPHA, DEFAULT_HIGHLIGHT_COLOR } from "~/utils/color";
import { isLexicalStateEmpty } from "~/utils/common";

const SECTION_KEYS: Record<string, string> = {
  [RECOMMENDATION_SECTION_KEY_AI]: "AI",
  content: "Content",
  actions: "Actions",
  translation: "Translation",
};

const DEFAULT_HIGHLIGHT = { color: DEFAULT_HIGHLIGHT_COLOR, isBackground: true };

const props = defineProps<{
  hideAiActionButton?: boolean;
  titleIsEmpty: boolean;
}>();

const userStore = useUserStore();

const editor = useLexicalComposer();
const editorEmpty = ref(false);
const selectionContentEmpty = ref(false);

const blockType = ref("paragraph");
const selectedElementKey = ref<string | null>(null);

const isLink = ref(false);
const isUnlinkedAutoLink = ref(false);
const isBold = ref(false);
const isItalic = ref(false);
const isUnderline = ref(false);
const isStrikethrough = ref(false);
const isCode = ref(false);
const listType = ref<ListType | null>(null);

const currentTextColor = ref("");
const currentBackgroundColor = ref("");

const isUnlinkedAutoLinkNode = (node: LexicalNode | null) => $isAutoLinkNode(node) && node.getIsUnlinked();

// Copied from https://github.com/wobsoriano/lexical-vue/blob/main/playground/src/components/ToolbarPlugin.vue
const updateToolbar = () => {
  const selection = $getSelection() as RangeSelection;
  if (!$isRangeSelection(selection)) {
    selectionContentEmpty.value = false;
    return;
  }

  const anchorNode = selection.anchor.getNode();
  const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
  const elementKey = element.getKey();
  const elementDOM = editor.getElementByKey(elementKey);
  if (elementDOM !== null) {
    selectedElementKey.value = elementKey;

    if ($isListNode(element)) {
      const parentList = $getNearestNodeOfType(anchorNode, ListNode) as ListNode;
      listType.value = parentList.getListType();
      blockType.value = parentList.getTag();
    } else {
      listType.value = null;
      blockType.value = $isHeadingNode(element) ? element.getTag() : element.getType();
    }
  }
  // Update text format
  isBold.value = selection.hasFormat("bold");
  isItalic.value = selection.hasFormat("italic");
  isUnderline.value = selection.hasFormat("underline");
  isStrikethrough.value = selection.hasFormat("strikethrough");
  isCode.value = selection.hasFormat("code");

  // Update links
  const node = getSelectedNode(selection);
  const parent = node.getParent();
  let child: LexicalNode | null = null;
  if ($isParagraphNode(node) || $isHeadingNode(node) || $isListItemNode(node)) {
    if (node.getChildren().length === 1) {
      child = node.getChildren()[0];
    }
  }
  const linkNode = $isLinkNode(parent) ? parent : $isLinkNode(node) ? node : $isLinkNode(child) ? child : null;

  if (linkNode) {
    isLink.value = true;
    if (isUnlinkedAutoLinkNode(linkNode)) {
      isUnlinkedAutoLink.value = true;
    } else {
      isUnlinkedAutoLink.value = false;
    }
  } else {
    isLink.value = false;
    isUnlinkedAutoLink.value = false;
  }

  // Update colors
  currentTextColor.value = $getSelectionStyleValueForProperty(selection, "color", "") || "";
  currentBackgroundColor.value = $getSelectionStyleValueForProperty(selection, "background-color", "") || "";

  selectionContentEmpty.value = !selection.isCollapsed() && selection.getTextContent().trim() === "";
};

const insertLink = () => {
  if (!isLink.value) {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, "");
  } else {
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
  }
};

const processOption = (option: TypeaheadOption) => ({
  title: option.label,
  kind: DropdownMenuItemKind.BUTTON,
  icon: option.icon,
  iconArgs: option.iconArgs,
  disabled: option.disabled,
  disabledReason: option.disabledReason,
  commandId: option.value === RecommendationKind.CUSTOM ? CommandId.EDITOR_USE_AI : undefined,
  onClick: () => {
    if (option.value === RecommendationKind.SPLIT) {
      editor.dispatchCommand(EVENT_SPLIT_TASK, undefined);
      return;
    }
    if (option.value === RecommendationKind.CONVERT_TO_TASKS) {
      editor.dispatchCommand(EVENT_CONVERT_TO_TASKS, undefined);
      return;
    }
    editor.dispatchCommand(EVENT_GENERATE_RECOMMENDATION, {
      kind: option.value as RecommendationKind | Language,
      parentKey: null,
    });
  },
});

const slashCommandsMenu = ref<InstanceType<typeof DropdownMenu> | null>(null);
const slashButtonDropdownOptions = computed(() => {
  const sections = getRecommendationTypeaheadSections(
    editor._config.namespace,
    false,
    editorEmpty.value,
    props.titleIsEmpty,
    selectionContentEmpty.value
  );
  return sections.map((section) => ({
    title: SECTION_KEYS[section.key] ?? section.key,
    showTitle: section.key !== RECOMMENDATION_SECTION_KEY_AI,
    items: section.options.map((option: TypeaheadOption) => {
      if (option.options) {
        return {
          title: option.label,
          kind: DropdownMenuItemKind.COMPONENT,
          disabled: option.disabled,
          disabledReason: option.disabledReason,
          component: GenericSubdropdown,
          componentArgs: {
            title: option.label,
            icon: option.icon,
            iconArgs: option.iconArgs,
            widthPixels: 320,
            sections: [
              {
                title: "Options",
                items: option.options.map(processOption),
              },
            ],
          },
        };
      }
      return processOption(option);
    }),
  }));
});

let unregisterListeners: () => void;

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        editorEmpty.value = isLexicalStateEmpty(editorState.toJSON(), undefined, true);
        updateToolbar();
      });
    }),
    editor.registerMutationListener(AutoLinkNode, () => {
      editor.getEditorState().read(() => {
        updateToolbar();
      });
    }),
    editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        editor.getEditorState().read(() => {
          updateToolbar();
        });
        return false;
      },
      COMMAND_PRIORITY_LOW
    )
  );
});

onUnmounted(() => {
  unregisterListeners?.();
});

const hasActiveColor = computed(() => !!currentTextColor.value || !!currentBackgroundColor.value);

const mostRecentColor = computed(() => {
  const stack = userStore.recentColorCombinedStack;
  return stack.length > 0 ? stack[0] : DEFAULT_HIGHLIGHT;
});

const colorButtonBackgroundColor = computed(() => {
  if (currentBackgroundColor.value) {
    return currentBackgroundColor.value;
  }
  if (hasActiveColor.value || !mostRecentColor.value.isBackground || !mostRecentColor.value.color) {
    return "";
  }
  return `${mostRecentColor.value.color}${BACKGROUND_ALPHA}`;
});

const colorButtonIconColor = computed(() => {
  if (currentTextColor.value) {
    return currentTextColor.value;
  }
  if (hasActiveColor.value || mostRecentColor.value.isBackground) {
    return "";
  }
  return mostRecentColor.value.color;
});

const toggleRecentColor = () => {
  const recent = mostRecentColor.value;

  if (hasActiveColor.value) {
    editor.dispatchCommand(EVENT_FORMAT_TEXT_COLOR, "");
    editor.dispatchCommand(EVENT_FORMAT_BACKGROUND_COLOR, "");
  } else if (recent.isBackground) {
    editor.dispatchCommand(EVENT_FORMAT_BACKGROUND_COLOR, recent.color ? `${recent.color}${BACKGROUND_ALPHA}` : "");
  } else {
    editor.dispatchCommand(EVENT_FORMAT_TEXT_COLOR, recent.color);
  }
};

const preserveEditorFocus = (event: MouseEvent) => {
  if (event.target instanceof Element && event.target.closest("button")) {
    event.preventDefault();
  }
};

const colorSections = [
  {
    title: "Change color",
    items: [
      {
        title: "Change color",
        kind: DropdownMenuItemKind.COMPONENT,
        noFocus: true,
        component: ChangeColorDropdown,
      },
    ],
  },
];
</script>

<template>
  <div
    class="flex items-stretch gap-1 px-1 text-lt"
    @mousedown.left="preserveEditorFocus"
    @click.stop="editor.focus()"
    @keydown.enter.stop="editor.focus()">
    <DropdownMenu
      v-if="!hideAiActionButton"
      ref="slashCommandsMenu"
      :popper-class="LEXICAL_OUTSIDE_CLASS"
      :sections="slashButtonDropdownOptions"
      enable-show-more
      :distance="8"
      class="py-1">
      <button
        type="button"
        class="group/animate-ai flex items-center gap-1 rounded-sm px-1 py-0.5"
        :class="slashCommandsMenu?.isOpen ? 'bg-md hover:bg-hvy' : 'hover:bg-md'">
        <RecommendationIcon class="icon-sm text-recommendation-base" />
        <span class="w-4 text-sm">AI</span>
      </button>
    </DropdownMenu>

    <TextFormatOptionsDropdown
      ref="dropdown"
      :block-type="blockType as TextBlockType"
      class="py-1"
      :class="!hideAiActionButton && 'border-l border-md pl-1'" />

    <div class="flex items-center gap-1 border-l border-md py-1 pl-1">
      <Tooltip :command-id="CommandId.EDITOR_BOLD">
        <button
          type="button"
          class="rounded-sm p-1"
          :class="isBold ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Format Bold"
          @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')">
          <BoldIcon class="icon-sm" />
        </button>
      </Tooltip>

      <Tooltip :command-id="CommandId.EDITOR_ITALICIZE">
        <button
          type="button"
          class="rounded-sm p-1"
          :class="isItalic ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Format Italics"
          @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')">
          <ItalicIcon class="icon-sm" />
        </button>
      </Tooltip>

      <Tooltip :command-id="CommandId.EDITOR_UNDERLINE">
        <button
          type="button"
          class="rounded-sm p-1"
          :class="isUnderline ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Format Underline"
          @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')">
          <UnderlineIcon class="icon-sm" />
        </button>
      </Tooltip>

      <Tooltip :command-id="CommandId.EDITOR_STRIKETHROUGH">
        <button
          type="button"
          class="rounded-sm p-1"
          :class="isStrikethrough ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Format Strikethrough"
          @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')">
          <StrikethroughIcon class="icon-sm" />
        </button>
      </Tooltip>

      <Tooltip :command-id="CommandId.EDITOR_SET_CODE_SNIPPET">
        <button
          type="button"
          class="rounded-sm p-1"
          :class="isCode ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Insert Code"
          @click="editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')">
          <CodeSnippetIcon class="icon-sm" />
        </button>
      </Tooltip>

      <Tooltip :command-id="CommandId.EDITOR_ADD_LINK">
        <button
          type="button"
          class="rounded-sm p-1"
          :class="isLink && !isUnlinkedAutoLink ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Insert Link"
          @click.stop="insertLink">
          <LinkIcon class="icon-sm" />
        </button>
      </Tooltip>
    </div>

    <div class="flex items-center border-l border-md py-1 pl-1">
      <Tooltip :command-id="CommandId.EDITOR_TEXT_OR_BG_COLOR">
        <div class="flex items-center">
          <button
            type="button"
            class="group/color-button relative overflow-hidden rounded-l p-1"
            :class="colorButtonBackgroundColor ? '' : hasActiveColor ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
            :style="colorButtonBackgroundColor ? { backgroundColor: colorButtonBackgroundColor } : undefined"
            aria-label="Toggle highlight"
            @click="toggleRecentColor">
            <div
              v-if="colorButtonBackgroundColor"
              class="pointer-events-none absolute inset-0 rounded-l opacity-0 transition-opacity group-hover/color-button:opacity-100 group-focus/color-button:opacity-100"
              :style="{ backgroundColor: colorButtonBackgroundColor }" />
            <ColorPickerIcon
              class="relative icon-sm"
              :style="colorButtonIconColor ? { color: colorButtonIconColor } : undefined" />
          </button>
          <DropdownMenu :sections="colorSections" :placement="Placement.BOTTOM" :width-pixels="216" :distance="8">
            <button
              type="button"
              class="flex items-center rounded-r px-0.5 py-1.5"
              :class="hasActiveColor ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
              aria-label="Color options">
              <ChevronDownIcon class="size-3" />
            </button>
          </DropdownMenu>
        </div>
      </Tooltip>
    </div>

    <div class="flex items-center gap-1 border-l border-md py-1 pl-1">
      <Tooltip :command-id="CommandId.EDITOR_SET_CHECKLIST">
        <button
          type="button"
          class="rounded-sm p-0.5"
          :class="listType === 'check' ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Set checklist"
          @click="formatCheckList(listType, editor)">
          <ChecklistIcon class="icon-md" />
        </button>
      </Tooltip>
      <Tooltip :command-id="CommandId.EDITOR_SET_NUMBERED_LIST">
        <button
          type="button"
          class="rounded-sm p-0.5"
          :class="listType === 'number' ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Set numbered list"
          @click="formatNumberedList(listType, editor)">
          <NumberedListIcon class="icon-md" />
        </button>
      </Tooltip>
      <Tooltip :command-id="CommandId.EDITOR_SET_BULLETED_LIST">
        <button
          type="button"
          class="rounded-sm p-0.5"
          :class="listType === 'bullet' ? 'bg-md hover:bg-hvy' : 'hover:bg-md'"
          aria-label="Set bulleted list"
          @click="formatBulletList(listType, editor)">
          <UnorderedListIcon class="icon-md" />
        </button>
      </Tooltip>
    </div>
  </div>
</template>
